import type { ISODate, MemberId } from '../contract.js';
import { addDays, compareDates, compareMonths, addMonths, monthFromKey, monthKey, monthOf } from '../dates.js';
import { draftDirty, peDirty } from './derive.js';
import { earliestDay, earliestMonthKey, entriesOf, isYou, todayMonthKey, todayOf } from './lookup.js';
import type { AppStore } from './store.js';
import type { AppState, Patch } from './types.js';

/**
 * Navigation and its choreography, ported from the locked prototype (03 §3).
 * Every choreographed action starts by cancelling the pending steps and stopping playback, so the
 * newest navigation always wins; CSS transitions then continue from where they are.
 */

const begin = (s: AppStore): void => { s.clearLater(); s.stopPlayback(); };

// ------------------------------------------------------------------ the shared cross-fade (03 §3.1)
export function fadeTo(s: AppStore, patch: Patch, keep = false, after?: () => void): void {
  begin(s);
  if (s.reduced()) { s.set({ lv: false, lvKeep: false, play: null, hoverP: null, ...patch }); after?.(); return; }
  s.set({ lv: true, lvKeep: keep, play: null, hoverP: null });
  s.later(200, () => s.set({ ...patch, lv: true }));
  s.later(240, () => { s.set({ lv: false }); after?.(); });
}

// ------------------------------------------------------------------ calendar
export function hoverTile(s: AppStore, date: ISODate): void {
  const st = s.getState();
  if (!st.cd && !st.lv && st.hover !== date) s.set({ hover: date });
}
export function clearHover(s: AppStore): void {
  const st = s.getState();
  if (!st.cd && st.hover !== null) s.set({ hover: null });
}
export function stepMonth(s: AppStore, dir: -1 | 1): void {
  const st = s.getState();
  if (!st.session || st.view !== 'cal') return;
  const next = addMonths(monthFromKey(st.month), dir);
  if (compareMonths(next, monthFromKey(earliestMonthKey(st))) < 0 || compareMonths(next, monthFromKey(todayMonthKey(st))) > 0) return;
  const key = monthKey(next);
  s.set({ month: key, hover: null });
  void s.loadMonth(key);
}

// ------------------------------------------------------------------ day (03 §3.2–3.4)
/** Whether a date can be opened: from the first day of the earliest month to today. */
export function isOpenDay(st: AppState, date: ISODate): boolean {
  return !!st.session && compareDates(date, earliestDay(st)) >= 0 && compareDates(date, todayOf(st)) <= 0;
}

export function openDay(s: AppStore, date: ISODate): void {
  if (s.getState().lv || !isOpenDay(s.getState(), date)) return;
  begin(s);
  void s.loadDay(date);
  const base: Patch = { view: 'day', date, hover: date, person: null, pp: null, play: null, hoverP: null };
  if (s.reduced()) { s.set({ ...base, cd: null, swap: 'still', hover: null }); return; }
  s.set({ ...base, cd: 'in-pre', swap: 'pre' });
  s.later(30, () => s.set({ cd: 'in', swap: 'rise' }));
  s.later(980, () => s.set({ cd: null, hover: null }));
  s.later(1500, () => s.set({ swap: 'still' }));
}

export function closeDay(s: AppStore): void {
  const st = s.getState();
  begin(s);
  if (st.view === 'person' && !s.reduced()) {
    s.set({ pp: 'out', play: null });
    s.later(320, () => { s.set({ view: 'day', person: null, pp: null }); closeDay(s); });
    return;
  }
  const month = monthKey(monthOf(st.date));
  void s.loadMonth(month);
  if (s.reduced()) { s.set({ view: 'cal', month, cd: null, person: null, pp: null, hover: null, play: null }); return; }
  s.set({ view: 'day', month, person: null, pp: null, cd: 'out-pre', hover: st.date, play: null });
  s.later(30, () => s.set({ cd: 'out' }));
  s.later(700, () => s.set({ view: 'cal', cd: null, hover: null }));
}

export function jumpDay(s: AppStore, date: ISODate): void {
  if (!isOpenDay(s.getState(), date)) return;
  begin(s);
  void s.loadDay(date);
  const base: Patch = { view: 'day', person: null, pp: null, date, hoverP: null, play: null, cd: null };
  if (s.reduced()) { s.set({ ...base, swap: 'still' }); return; }
  s.set({ ...base, swap: 'pre' });
  s.later(30, () => s.set({ swap: 'quick' }));
  s.later(700, () => s.set({ swap: 'still' }));
}
export const stepDay = (s: AppStore, dir: -1 | 1): void => jumpDay(s, addDays(s.getState().date, dir));

/**
 * A day's posts arrived after its tiles could have popped in (the choreography already ended).
 * Implementation default (not from the prototype, which has its data at once): pop them in with
 * the quick stagger used when stepping days.
 */
export function popLateDay(s: AppStore, date: ISODate): void {
  const st = s.getState();
  if (st.view !== 'day' || st.date !== date || st.cd || st.lv || st.swap !== 'still' || s.reduced()) return;
  if (!entriesOf(st, date).length) return;
  s.set({ swap: 'pre' });
  s.later(30, () => { if (s.getState().swap === 'pre') s.set({ swap: 'quick' }); });
  s.later(700, () => { if (s.getState().swap === 'quick') s.set({ swap: 'still' }); });
}

export function hoverPerson(s: AppStore, id: MemberId): void {
  const st = s.getState();
  if (st.view === 'day' && !st.cd && !st.lv && st.hoverP !== id) s.set({ hoverP: id });
}
export function unhoverPerson(s: AppStore, id: MemberId): void {
  if (s.getState().hoverP === id) s.set({ hoverP: null });
}

// ------------------------------------------------------------------ post (03 §3.6–3.8)
export function openPerson(s: AppStore, id: MemberId): void {
  if (s.getState().lv) return;
  begin(s);
  // A post shows media the day has not shown yet. Its addresses came with the day and last five minutes,
  // so a day that was read longer ago is read again here; a fresh one is left alone.
  void s.loadDay(s.getState().date);
  // Implementation default (03 §3.15): a post opened while its day is still opening ends that opening
  // at once. The prototype left it unfinished, which kept the day arrows disabled afterwards.
  const settle: Patch = s.getState().cd ? { cd: null, hover: null } : {};
  if (s.reduced()) { s.set({ ...settle, view: 'person', person: id, pp: null, play: null, slide: 0 }); return; }
  s.set({ ...settle, view: 'person', person: id, pp: 'in-pre', play: null, swap: 'still', slide: 0 });
  s.later(30, () => s.set({ pp: 'in' }));
  s.later(1500, () => s.set({ pp: null }));
}

export function cycle(s: AppStore, dir: -1 | 1): void {
  const st = s.getState();
  if (st.view !== 'person' || st.pp === 'out' || st.pp === 'in-pre' || st.lv) return;
  const entries = entriesOf(st, st.date), n = entries.length;
  if (n < 2) return;
  const index = Math.max(0, entries.findIndex((entry) => entry.memberId === st.person));
  s.stopPlayback();
  // The next post's media has not been shown yet either (see openPerson).
  void s.loadDay(st.date);
  s.set({ person: entries[(index + dir + n) % n]?.memberId ?? st.person, cyc: st.cyc + 1, slide: dir, play: null });
}

/**
 * "View your post" (owner, 4 October 2026): your post for today, from wherever the island offers it.
 * On today's day it opens like any square; on someone else's post today it slides over like the
 * person arrows; from anywhere else it cross-fades there and reads the day.
 */
export function openOwnPost(s: AppStore): void {
  const st = s.getState();
  if (st.lv || !st.session || st.view === 'compose' || st.pe) return;
  const me = st.session.me.id, today = todayOf(st);
  if (st.view === 'person' && st.date === today) {
    if (st.person === me || st.pp === 'out' || st.pp === 'in-pre') return;
    const entries = entriesOf(st, today), from = entries.findIndex((entry) => entry.memberId === st.person), to = entries.findIndex((entry) => entry.memberId === me);
    s.stopPlayback();
    void s.loadDay(today);
    s.set({ person: me, cyc: st.cyc + 1, slide: to >= 0 && to < from ? -1 : 1, play: null });
    return;
  }
  if (st.view === 'day' && st.date === today) { openPerson(s, me); return; }
  void s.loadDay(today);
  fadeTo(s, { view: 'person', date: today, person: me, pp: null, cd: null, swap: 'still', hover: null, hoverP: null, pfx: null, tray: null, slide: 0 });
}

export function closePerson(s: AppStore): void {
  begin(s);
  if (s.reduced()) { s.set({ view: 'day', person: null, pp: null, play: null }); return; }
  s.set({ pp: 'out', play: null });
  s.later(460, () => s.set({ view: 'day', person: null, pp: null }));
}

// ------------------------------------------------------------------ profile (03 §3.9–3.10)
export function openProfile(s: AppStore, id: MemberId, from: 'cal' | 'day' | 'person'): void {
  const st = s.getState();
  if (st.lv || st.pe || (st.view === 'profile' && st.profileOf === id)) return;
  const ret = st.view === 'profile' ? st.ret : from;
  const keep = !isYou(st, id) && st.view === 'person' && st.person === id;
  void s.loadProfile(id);
  fadeTo(s, { view: 'profile', profileOf: id, ret, pfx: 'in-pre', cd: null, pp: null, swap: 'still', hover: null, pe: null, pdrag: null, sel: null, txt: null, tray: null }, keep, () => {
    if (s.reduced()) { s.set({ pfx: null }); return; }
    s.set({ pfx: 'in' });
    s.later(1100, () => s.set({ pfx: null }));
  });
}
/** Your picture and name in the island: your own profile, from wherever you are. */
export function openOwnProfile(s: AppStore): void {
  const st = s.getState();
  if (st.lv || !st.session) return;
  openProfile(s, st.session.me.id, st.view === 'day' || st.view === 'person' ? st.view : 'cal');
}

export function backFromProfile(s: AppStore): void {
  const st = s.getState(), r = st.ret || 'cal';
  if (st.lv || st.pe) return;
  const view = r === 'person' && !st.person ? 'day' : r;
  fadeTo(s, { view, pfx: null, pp: null, cd: null, swap: 'still', hover: null }, r === 'person' && st.person === st.profileOf && !isYou(st, st.profileOf));
}

// ------------------------------------------------------------------ home (03 §3.12)
export function homeNow(s: AppStore): void {
  const st = s.getState(), today = todayOf(st), home = todayMonthKey(st);
  const pulse = () => {
    s.set({ hover: today });
    s.later(1100, () => { const now = s.getState(); if (now.view === 'cal' && now.hover === today) s.set({ hover: null }); });
  };
  void s.loadMonth(home);
  if (st.view === 'cal' && !st.cd) { s.clearLater(); s.set({ month: home, homeAfter: false }); pulse(); return; }
  if ((st.view === 'day' || st.view === 'person') && st.date === today && !st.cd && !st.pp) { s.set({ homeAfter: false }); closeDay(s); return; }
  fadeTo(s, {
    view: 'cal', month: home, person: null, pp: null, cd: null, hover: null, hoverP: null, swap: 'still', pfx: null, homeAfter: false,
    pe: null, peStatus: 'idle', peAsk: false, draft: null, cStatus: 'idle', cAsk: null, sel: null, txt: null, tray: null, pdrag: null, dropCell: null,
  }, false, pulse);
}

export function goHome(s: AppStore): void {
  const st = s.getState();
  if (st.lv || !st.session) return;
  if (st.view === 'compose') {
    if (st.cStatus === 'saving') return;
    if (draftDirty(st)) { s.set({ cAsk: 'discard', homeAfter: true, tray: null, sel: null, txt: null, pdrag: null }); return; }
  } else if (st.view === 'profile' && st.pe) {
    if (st.peStatus === 'saving') return;
    if (peDirty(st)) { s.set({ peAsk: true, homeAfter: true, tray: null, sel: null, txt: null, pdrag: null }); return; }
  }
  homeNow(s);
}
